import React, { Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Avatar
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  ArrowForward as ArrowRightIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon
} from '@material-ui/icons';
import KEYLogo from 'assets/kwt_product_logo.png';
import { Mastercode, OrderItem } from 'types/schema-generated-types';

export type Props = {
  mastercode: Mastercode;
  onActivateMastercodeClick: () => void;
  onTransferMastercodeClick: () => void;
};

const OrderItemListItem = ({ orderItem }: { orderItem: OrderItem }) => {
  const classes = useStyles();

  return (
    <Fragment>
      <ListItem>
        <ListItemText
          primaryTypographyProps={{ className: classes.orderListItem }}
          primary={`${orderItem?.product?.code} - ${orderItem?.product?.name}` || ''}
        />
        <ListItemSecondaryAction>
          <Typography>{orderItem.licenseCount}</Typography>
        </ListItemSecondaryAction>
      </ListItem>
      <Divider component="li" />
    </Fragment>
  );
};

const OrderItemList = ({ orderItems }: { orderItems: OrderItem[] }) => (
  <Grid item>
    <List dense>
      <Divider component="li" />
      {orderItems.map(
        (orderItem: OrderItem): ReactElement => (
          <OrderItemListItem key={`${orderItem.id}-product-item`} orderItem={orderItem} />
        )
      )}
    </List>
  </Grid>
);

const getLicensesCount = (orderItems: Array<OrderItem>): number =>
  orderItems.reduce((licenseCount, orderItem) => licenseCount + orderItem.licenseCount, 0);

const MastercodeCard = ({
  mastercode,
  onActivateMastercodeClick,
  onTransferMastercodeClick
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('Order');

  const [firstOrderItem] = mastercode.orderItems || [];

  const familyTitle = firstOrderItem?.product?.family.name || '';
  const familyIcon = firstOrderItem?.product?.family.iconUrl || KEYLogo;
  const licensesCount = getLicensesCount(mastercode.orderItems || []);
  const entity = licensesCount > 1 ? 'Licenses' : 'License';

  return (
    <Grid
      component={Paper}
      elevation={0}
      container
      spacing={0}
      direction="column"
      className={classes.container}
    >
      <Grid
        container
        spacing={0}
        alignItems="center"
        justify="space-between"
        className={classes.headerContainer}
      >
        <Grid sm={4} item container direction="row">
          <Grid>
            <Avatar
              variant="square"
              src={familyIcon}
              alt="product-logo"
              className={classes.productLogo}
            />
          </Grid>
          <Grid>
            <Typography variant="h5">
              {familyTitle} - {licensesCount} {entity}
            </Typography>
            <Typography>Activation Code: {mastercode.code}</Typography>
          </Grid>
        </Grid>
        <Grid item>
          {mastercode.dateUnlocked ? (
            <Typography variant="subtitle1" className={classes.alreadyActivatedText}>
              <CheckCircleIcon fontSize="small" className={classes.alreadyActivatedIcon} />
              {t('Licenses already activated')}
            </Typography>
          ) : (
            <>
              <Button
                variant="text"
                size="small"
                className={classes.headerButton}
                disableElevation
                onClick={onTransferMastercodeClick}
                startIcon={<ArrowRightIcon />}
              >
                Transfer Code
              </Button>
              <Button
                variant="outlined"
                size="small"
                className={classes.headerButton}
                color="secondary"
                disableElevation
                onClick={onActivateMastercodeClick}
                startIcon={<CheckIcon />}
              >
                Activate Code
              </Button>
            </>
          )}
        </Grid>
      </Grid>
      <OrderItemList orderItems={mastercode?.orderItems || []} />
    </Grid>
  );
};

export default MastercodeCard;

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: theme.spacing(3)
  },
  alreadyActivatedIcon: {
    marginRight: theme.spacing(0.5)
  },
  alreadyActivatedText: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.success.main
  },
  productLogo: {
    height: 50,
    width: 50,
    marginRight: theme.spacing(1.5)
  },
  headerContainer: {
    marginBottom: theme.spacing()
  },
  headerButton: {
    marginLeft: theme.spacing(1)
  },
  detailsContainer: {
    paddingTop: 0
  },
  orderListItem: {
    textTransform: 'none'
  }
}));
